import { Avatar, Box, Flex, IconButton, Tag, TagText, Text } from "@govlaunch/web";
import Tooltip from "@govlaunch/web/src/tooltip/Tooltip";
import React, { forwardRef, ReactElement } from "react";
import { AddedToStackIcon, AddToStackIcon } from "./components/icons";
import { IProductCardFieldsFragment } from "./fragments/__generated__/ProductCardFieldsFragment.generated";
import { useIntl } from "react-intl";

type TProductTranslationLink = (slug: string) => ReactElement | null;

interface IProductCardProps {
  product: IProductCardFieldsFragment;
  canToggleInStack?: boolean;
  onToggleInStack?: (product: IProductCardFieldsFragment) => void;
  translationLinkWrap?: TProductTranslationLink;
}

const ProductCard = forwardRef<any, IProductCardProps>(
  ({ product, canToggleInStack, onToggleInStack, translationLinkWrap, ...props }, ref) => {
    const intl = useIntl();

    const onToggleStack = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (typeof onToggleInStack === "function" && canToggleInStack) {
        onToggleInStack(product);
      }
    };

    return (
      <Flex
        ref={ref}
        as="a"
        bg="white"
        border="solid 1px"
        borderColor="gray.100"
        borderRadius="md"
        cursor="pointer"
        direction="column"
        paddingBottom={8}
        position="relative"
        transition="0.2s all"
        _hover={{
          shadow: "lg",
        }}
        {...props}
      >
        <Box mb={translationLinkWrap ? 3 : 6}>
          {translationLinkWrap && (
            <Flex direction="row" alignItems="center" justifyContent="center" mt={2}>
              {translationLinkWrap(product.slug)}
            </Flex>
          )}
          <Box position="absolute" top={2} right={2}>
            {product.governmentsUsingCount !== null && product.governmentsUsingCount > 0 && (
              <Tooltip
                label={intl.formatMessage({ defaultMessage: 'Governments Using', id: 'zSwNnO' })}
                aria-label={intl.formatMessage({ defaultMessage: 'Governments Using', id: 'zSwNnO' })}
                color="blue.50"
                bg="blue.600"
                placement="top"
              >
                <Box
                  as="span"
                  paddingX={2}
                  paddingY={1}
                  border="solid 1px"
                  borderColor="blue.800"
                  color="blue.800"
                  borderRadius="md"
                  fontSize={10}
                >
                  {product.governmentsUsingCount}
                </Box>
              </Tooltip>
            )}

            {canToggleInStack && !product.inViewerStack && (
              <Tooltip
                label={intl.formatMessage({ defaultMessage: 'Add to Stack', id: '7rcJw5' })}
                aria-label={intl.formatMessage({ defaultMessage: 'Add to Stack', id: '7rcJw5' })}
                bg="teal.600"
              >
                <IconButton
                  aria-label={intl.formatMessage({ defaultMessage: 'Add to Stack', id: '7rcJw5' })}
                  icon={AddToStackIcon}
                  onClick={onToggleStack}
                  height="24px"
                  width="24px"
                />
              </Tooltip>
            )}

            {canToggleInStack && product.inViewerStack && (
              <Tooltip
                label={intl.formatMessage({ defaultMessage: 'Click to Remove from Stack', id: 't9cOZx' })}
                aria-label={intl.formatMessage({ defaultMessage: 'Click to Remove from Stack', id: 't9cOZx' })}
                color="red.50"
                bg="red.600"
                placement="top"
              >
                <IconButton
                  aria-label={intl.formatMessage({ defaultMessage: 'Remove from Stack', id: 'fMgmCu' })}
                  icon={AddedToStackIcon}
                  onClick={onToggleStack}
                  height="24px"
                  width="24px"
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box marginX={8}>
          <Flex alignItems="center">
            <Avatar
              src={product.logo}
              name={product.name}
              size="lg"
              variant={product.isDisruptor ? "disruptor" : null}
            />

            <Box overflow="hidden">
              <Text fontSize="xl" color="blue.600" fontWeight="bold" mt={0} ml={3} clamp={2} title={product.name}>
                {product.name}
              </Text>
            </Box>
          </Flex>

          <Text fontSize="sm" mt={2} mb={4} clamp={2}>
            {product.description}
          </Text>

          {product.tags && product.tags.length > 0 && (
            <Box mt="auto">
              <Tag maxWidth="calc(100% - 24px)" title={product.tags[0].name}>
                <TagText>{product.tags[0].name}</TagText>
              </Tag>

              {product.tags.length > 1 && (
                <Box as="span" color="blue.800" fontSize="sm" marginLeft={2} fontWeight="medium">
                  +{product.tags.length - 1}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Flex>
    );
  },
);

ProductCard.displayName = "ProductCard";

export default ProductCard;
